import React, { useEffect, useState, useMemo } from 'react';
import { Button, Label, Input } from '@iq/react-components';
import { useSelector, useDispatch } from 'react-redux';

import { getHasPermission } from '../../../bundles/auth';
import { createBookmark, updateBookmark } from '../../../bundles/bookmarks';
import { getTags } from '../../../bundles/tags';

import ComponentSelect from '../../ComponentSelect';
import TagSelect from '../../TagSelect';

const formatSeconds = (seconds, target) => {
  if (!seconds) return 0;

  switch (target) {
    case 'seconds': {
      return Math.floor(seconds % 60);
    }
    case 'minutes': {
      return Math.floor(seconds / 60);
    }
    case 'hours': {
      return Math.floor(seconds / 60 / 60);
    }
    default: {
      return seconds;
    }
  }
};

const BookmarkPanel = ({ file, fileInfo, bookmark: defaultBookmark = {}, onClose }) => {
  // NOTE: We're memoing the initial data here to prevent it from being
  // overwritten if the video is paused a ms after the dialog was opened. We
  // can probably clean this up a bit.
  const initialBookmark = useMemo(() => {
    if (!defaultBookmark) {
      return {};
    }

    return defaultBookmark;
  }, [defaultBookmark]);

  const { bookmarkData: initialBookmarkdata = {} } = initialBookmark;

  const dispatch = useDispatch();
  const [validationError, setValidationError] = useState(null);

  const availableTags = useSelector(getTags);
  const [bookmarkData, setBookmarkData] = useState(initialBookmarkdata);
  const [name, setName] = useState(initialBookmark.name || '');
  const [components, setComponents] = useState(initialBookmark.components || file.components);
  const [tags, setTags] = useState(
    (initialBookmark.tags || file.tags || []).filter((tagId) =>
      availableTags.find((tag) => tag.id === tagId)
    )
  );

  const [displayCreateSpinner, setDisplayCreateSpinner] = useState(false);
  const isCreatingBookmark = useSelector((state) => state.bookmarks.isCreating);

  const [hours, setHours] = useState(formatSeconds(bookmarkData.time, 'hours'));
  const [minutes, setMinutes] = useState(formatSeconds(bookmarkData.time, 'minutes'));
  const [seconds, setSeconds] = useState(formatSeconds(bookmarkData.time, 'seconds'));
  const [isCreating, setCreating] = useState(isCreatingBookmark);

  const canCreateTags = useSelector((state) =>
    getHasPermission(state, 'tags/Write', { org: file?.org, site: file?.site })
  );

  useEffect(() => {
    if (isCreatingBookmark) {
      setCreating(true);
    }
  }, [isCreatingBookmark]);

  useEffect(() => {
    if (isCreating && !isCreatingBookmark) {
      onClose();
    }
  }, [isCreating, isCreatingBookmark, onClose]);

  const onSubmit = (event) => {
    setValidationError(null);
    event.preventDefault();

    setDisplayCreateSpinner(true);

    if (file.mime.includes('video')) {
      const time = hours * 60 * 60 + minutes * 60 + seconds;

      if (time > fileInfo.duration) {
        setValidationError('Time for bookmark is not valid for current video!');
        setDisplayCreateSpinner(false);
        return;
      }

      bookmarkData.time = time;
    }

    const fileId = file.file ? file.file.id : file.id;

    const bookmark = {
      tags,
      components,
      name,
      bookmarkData,
    };

    if (initialBookmark.id) {
      dispatch(updateBookmark(fileId, initialBookmark.id, bookmark));
    } else {
      dispatch(createBookmark(fileId, bookmark));
    }
  };

  const title = useMemo(
    () => (initialBookmark.id ? 'Edit bookmark' : 'Create bookmark'),
    [initialBookmark]
  );

  const cta = useMemo(
    () => (initialBookmark.id ? 'Update bookmark' : 'Create bookmark'),
    [initialBookmark]
  );

  const renderBookMarkOptions = () => {
    if (file.mime.includes('pdf')) {
      return (
        <div className="row">
          <div className="column">
            <Label>
              Page
              <Input
                type="number"
                id="start-page"
                min="1"
                max={fileInfo.pages}
                defaultValue={bookmarkData.page}
                onChange={(event) => {
                  setBookmarkData({ ...bookmarkData, page: event.target.value });
                }}
                required
              />
            </Label>
          </div>
        </div>
      );
    }

    if (file.mime.includes('video')) {
      return (
        <div className="row">
          <div className="column">
            <Label>
              Time
              <div className="row video-time-row">
                <Input
                  type="number"
                  id="start-time-h"
                  min="0"
                  max="59"
                  value={hours}
                  onChange={(event) => {
                    setHours(event.target.value);
                  }}
                />{' '}
                :
                <Input
                  type="number"
                  id="start-time-min"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={(event) => {
                    setMinutes(event.target.value);
                  }}
                />{' '}
                :
                <Input
                  type="number"
                  id="start-time-s"
                  min="0"
                  max="59"
                  value={seconds}
                  onChange={(event) => {
                    setSeconds(event.target.value);
                  }}
                />
              </div>
            </Label>
          </div>
        </div>
      );
    }

    return <div> No bookmark option available for file </div>;
  };

  return (
    <div className="bookmark-panel">
      <div className="bookmark-panel-title">{title}</div>
      <form onSubmit={onSubmit}>
        {validationError && <div className="validation-error-message"> {validationError} </div>}
        {renderBookMarkOptions()}
        <div className="row">
          <div className="column">
            <Label>
              Name
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </Label>
          </div>
        </div>
        <div className="row">
          <div className="column">
            <Label htmlFor="components-selector">Components</Label>
            <ComponentSelect
              value={components}
              onChange={setComponents}
            />
          </div>
        </div>
        <div className="row">
          <div className="column">
            <Label htmlFor="tags-selector">Tags</Label>
            <TagSelect
              creatable={canCreateTags}
              onChange={setTags}
              value={tags}
              placeholder="Search or create..."
              category="files"
            />
          </div>
        </div>
        <div className="row buttons">
          <Button
            onClick={onClose}
            activity="secondary"
            type="button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={displayCreateSpinner}
          >
            {cta}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BookmarkPanel;
